import { useState } from 'react';
import { doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { loadUserProgress, getLevel } from '../components/progressManager';
import { askGPT } from '../components/OpenAIService';
import { useNavigate } from 'react-router-dom'; // Importation de useNavigate pour la redirection

export const ManageCompetence = (user, handleSnackbarOpen, currentCompetence, setCurrentCompetence) => {
  const [competenceProgress, setCompetenceProgress] = useState({});
  const [currentCompetenceProgress, setCurrentCompetenceProgress] = useState(0);
  const [level, setLevel] = useState('Débutant');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const navigate = useNavigate();  // Initialisation de navigate pour la redirection

  // Chargement d'une compétence par ID
  const loadCompetenceById = async (competenceId) => {
    if (!competenceId || !user) return;

    setLoading(true);
    try {
      const competenceRef = doc(db, 'users', user.uid, 'competences', competenceId);
      const docSnap = await getDoc(competenceRef);

      if (docSnap.exists()) {
        setCurrentCompetence(docSnap.data());  // Utiliser setCurrentCompetence passé en paramètre
      } else {
        setError('Compétence non trouvée');
      }
    } catch (err) {
      setError('Erreur lors de la récupération de la compétence');
    } finally {
      setLoading(false);
    }
  };

  // Sélection d'une compétence
  const handleSelectCompetence = async (competence) => {
    if (!competence || !competence.id) {
      setError('Compétence invalide ou manquante');
      return;
    }

    setCurrentCompetence(competence);  // Utiliser setCurrentCompetence passé en paramètre
    try {
      const progressData = await loadUserProgress(user.uid);
      setCompetenceProgress(progressData);

      const selectedProgress = progressData[competence.id]?.progress || 0;
      setCurrentCompetenceProgress(selectedProgress);
      setLevel(getLevel(selectedProgress));

      const message = await askGPT(
        `Rédige un message personnel et engageant de 50 mots pour inviter le destinataire à développer la compétence suivante avec Aladinn : ${competence.title}`
      );
      setGeneratedMessage(message);
      setCustomMessage(message);
    } catch (error) {
      setError("Erreur lors du chargement de la progression : " + error.message);
      handleSnackbarOpen('error', error.message);
    }
  };

  // Mise à jour du titre et du cours
  const handleCompetenceUpdate = async (event) => {
    if (loading || isEditingTitle) return; // Empêcher les mises à jour simultanées

    setLoading(true);
    const newTitle = event.target.innerText.trim();

    if (newTitle === currentCompetence.title) {
      setIsEditingTitle(false);
      setLoading(false);
      return; // Si le titre n'a pas changé, on ne fait rien
    }

    // Mettre à jour le titre et regénérer le cours
    try {
      const competenceRef = doc(db, 'users', user.uid, 'competences', currentCompetence.id);
      const prompt = `Explique "${newTitle}" sous forme de texte brut, en 200 mots maximum.
                     Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché.
                     Retourne uniquement le texte brut avec des paragraphes séparés par deux sauts de ligne.`;

      const newCours = await askGPT(prompt);

      // Mettre à jour la compétence dans la base de données
      await updateDoc(competenceRef, {
        title: newTitle,
        cours: newCours
      });

      // Mettre à jour l'état local pour réagir au changement
      setCurrentCompetence((prevCompetence) => ({
        ...prevCompetence,
        title: newTitle,
        cours: newCours
      }));

      setIsEditingTitle(false);
      handleSnackbarOpen('success', 'Compétence et cours mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour :', error);
      handleSnackbarOpen('error', 'Erreur lors de la mise à jour de la compétence');
    } finally {
      setLoading(false); // Fin de la mise à jour
    }
  };

  // Mise à jour de la progression
  const handleProgressUpdate = async () => {
    if (user && currentCompetence) {
      try {
        const updatedProgress = await loadUserProgress(user.uid);
        setCompetenceProgress(updatedProgress);
        const newProgress = updatedProgress[currentCompetence.id]?.progress || 0;
        setCurrentCompetenceProgress(newProgress);
        setLevel(getLevel(newProgress));
      } catch (error) {
        setError("Erreur lors de la mise à jour de la progression : " + error.message);
        handleSnackbarOpen('error', error.message);
      }
    }
  };

  // Suppression d'une compétence
  const handleDeleteCompetence = async () => {
    console.log("handleDeleteCompetence : user, currentCompetence", user, currentCompetence);
    if (!user || !currentCompetence) return;

    try {
      const competenceRef = doc(db, 'users', user.uid, 'competences', currentCompetence.id);
      await deleteDoc(competenceRef);
      setCurrentCompetence(null);  // Réinitialise la compétence actuelle
      handleSnackbarOpen('success', 'Compétence supprimée avec succès');
      
      // Redirection vers la liste des compétences après la suppression
      navigate('/');  // Redirige vers la liste des compétences
    } catch (error) {
      console.error('Erreur lors de la suppression :', error);
      handleSnackbarOpen('error', 'Erreur lors de la suppression de la compétence');
    }
  };

  // Régénération du cours
  const handleRegenerateCourse = async () => {
    if (!currentCompetence) return;

    try {
      const prompt = `Explique "${currentCompetence.title}" sous forme de texte brut, en 200 mots maximum.
                     Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché.
                     Retourne uniquement le texte brut avec des paragraphes séparés par deux sauts de ligne.`;

      const newCours = await askGPT(prompt);

      const competenceRef = doc(db, 'users', user.uid, 'competences', currentCompetence.id);
      await updateDoc(competenceRef, { cours: newCours });

      setCurrentCompetence((prev) => ({
        ...prev,
        cours: newCours
      }));

      handleSnackbarOpen('success', 'Cours régénéré avec succès');
    } catch (error) {
      console.error('Erreur lors de la régénération du cours :', error);
      handleSnackbarOpen('error', 'Erreur lors de la régénération du cours');
    }
  };

  // Pour démarrer l'édition du titre
  const handleEditTitle = () => {
    setIsEditingTitle(true);
  };

  return {
    currentCompetence,
    competenceProgress,
    currentCompetenceProgress,
    level,
    error,
    loading,
    isEditingTitle,
    generatedMessage,
    customMessage,
    setCustomMessage,
    handleCompetenceUpdate,
    handleProgressUpdate,
    handleDeleteCompetence,
    handleRegenerateCourse,
    handleEditTitle,
    handleSelectCompetence,
    loadCompetenceById
  };
};
